import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MapPin, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, DemoBadge, Semaphore, NativeSelect } from "@/components/common";
import { fmtD } from "@/lib/format";

const toDate = (v) => (v ? v.slice(0, 10) : "");

export const CommessaDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const [f, setF] = useState({});
  const [resps, setResps] = useState([]);
  useEffect(() => {
    if (!open) return;
    setF(initial ? { ...initial, start_date: toDate(initial.start_date), end_date: toDate(initial.end_date), services: (initial.services || []).join(", ") }
      : { code: "", name: "", comune: "", start_date: "", end_date: "", responsabile_id: "", services: "", notes: "", status: "attiva" });
    api.get("/users", { params: { role: "responsabile" } }).then((r) => setResps(r.data));
  }, [open, initial]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    const body = { code: f.code, name: f.name, comune: f.comune, notes: f.notes || "", status: f.status || "attiva",
      start_date: f.start_date ? new Date(f.start_date).toISOString() : null, end_date: f.end_date ? new Date(f.end_date).toISOString() : null,
      responsabile_id: f.responsabile_id || null, services: (f.services || "").split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      const { data } = initial ? await api.put(`/commesse/${initial.id}`, body) : await api.post("/commesse", body);
      toast.success("Commessa salvata");
      onOpenChange(false);
      onSaved(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl rounded-2xl" data-testid="commessa-dialog">
        <DialogHeader><DialogTitle className="text-xl font-extrabold">{initial ? "Modifica commessa" : "Nuova commessa"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1"><Label>Codice *</Label><Input value={f.code || ""} onChange={(e) => set("code", e.target.value)} data-testid="commessa-form-code" /></div>
          <div className="space-y-1"><Label>Comune *</Label><Input value={f.comune || ""} onChange={(e) => set("comune", e.target.value)} data-testid="commessa-form-comune" /></div>
          <div className="space-y-1 sm:col-span-2"><Label>Denominazione *</Label><Input value={f.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="commessa-form-name" /></div>
          <div className="space-y-1"><Label>Data inizio</Label><Input type="date" value={f.start_date || ""} onChange={(e) => set("start_date", e.target.value)} data-testid="commessa-form-start" /></div>
          <div className="space-y-1"><Label>Data fine</Label><Input type="date" value={f.end_date || ""} onChange={(e) => set("end_date", e.target.value)} data-testid="commessa-form-end" /></div>
          <div className="space-y-1 sm:col-span-2"><Label>Responsabile</Label>
            <NativeSelect className="w-full" testid="commessa-form-responsabile" value={f.responsabile_id} onChange={(v) => set("responsabile_id", v)}>
              <option value="">—</option>{resps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </NativeSelect></div>
          <div className="space-y-1 sm:col-span-2"><Label>Servizi gestiti (separati da virgola)</Label><Input value={f.services || ""} onChange={(e) => set("services", e.target.value)} data-testid="commessa-form-services" /></div>
          <div className="space-y-1 sm:col-span-2"><Label>Note</Label><Textarea value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
        </div>
        <DialogFooter><Button onClick={save} className="rounded-xl font-bold" data-testid="commessa-form-submit">Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Commesse() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    api.get("/commesse").then((r) => setList(r.data));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const canEdit = ["ceo", "amministrazione"].includes(user.role);
  return (
    <div data-testid="commesse-page">
      <PageHeader title="Commesse" subtitle="Contratti, cimiteri, squadre e personale"
        actions={canEdit && <Button onClick={() => setOpen(true)} className="h-11 rounded-xl font-bold" data-testid="commesse-new-button"><Plus className="mr-1 h-4 w-4" />Nuova commessa</Button>} />
      <div className="grid gap-4 md:grid-cols-2">
        {list?.map((c) => (
          <Link key={c.id} to={`/commesse/${c.id}`} className="card-soft tile-press block p-5" data-testid={`commessa-card-${c.code}`}>
            <div className="flex items-center gap-2"><Semaphore color={c.stats.overdue ? "red" : c.stats.to_verify ? "yellow" : "green"} /><span className="font-mono text-sm font-bold">{c.code}</span><DemoBadge show={c.is_demo} /><ChevronRight className="ml-auto h-4 w-4 text-zinc-400" /></div>
            <p className="mt-2 text-lg font-bold">{c.name}</p>
            <p className="text-sm text-muted-foreground">{c.comune} · {fmtD(c.start_date)} → {fmtD(c.end_date)} · Resp. {c.responsabile_name || "—"}</p>
            <div className="mt-3 flex flex-wrap gap-2">{c.cimiteri.map((x) => <span key={x.id} className="inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-1 text-xs font-semibold"><MapPin className="h-3 w-3" />{x.name}</span>)}</div>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-xl bg-muted p-2"><p className="text-xl font-extrabold">{c.stats.open}</p><p className="text-xs">aperti</p></div>
              <div className="rounded-xl bg-red-50 p-2 text-red-700"><p className="text-xl font-extrabold">{c.stats.overdue}</p><p className="text-xs">scaduti</p></div>
              <div className="rounded-xl bg-amber-50 p-2 text-amber-800"><p className="text-xl font-extrabold">{c.stats.to_verify}</p><p className="text-xs">da verificare</p></div>
            </div>
          </Link>
        ))}
      </div>
      {canEdit && <CommessaDialog open={open} onOpenChange={setOpen} onSaved={load} />}
    </div>
  );
}
